import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Table,
  Group,
  Badge,
  ActionIcon,
  Menu,
  TextInput,
  Stack,
  Alert,
  Paper,
  Checkbox,
  Button,
  Drawer,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSearch,
  IconDotsVertical,
  IconDownload,
  IconUpload,
  IconEye,
  IconAlertCircle,
  IconRefresh,
  IconDeviceDesktop,
} from '@tabler/icons-react';
import {
  useFogHosts,
  useFogImages,
  type FogHost,
} from '@/hooks';
import { DeployImageModal, CaptureImageModal, FogLoadingOverlay } from '@/components/fog';

export function FogHostsPage() {
  const { data, isLoading, error, refetch, isRefetching } = useFogHosts();
  const { data: imagesData } = useFogImages();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedHost, setSelectedHost] = useState<FogHost | null>(null);
  const [deployModalOpen, { open: openDeployModal, close: closeDeployModal }] = useDisclosure(false);
  const [captureModalOpen, { open: openCaptureModal, close: closeCaptureModal }] = useDisclosure(false);
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [pageVisible, setPageVisible] = useState(false);
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    setPageVisible(true);
  }, []);

  // Animate rows
  useEffect(() => {
    if (data?.hosts) {
      data.hosts.forEach((_, index) => {
        setTimeout(() => {
          setVisibleRows((prev) => new Set([...prev, index]));
        }, index * 50);
      });
    }
  }, [data?.hosts]);

  const hosts = data?.hosts || [];
  const images = imagesData?.images || [];

  // Filter hosts
  const filteredHosts = hosts.filter((host) => {
    const matchesSearch =
      !searchQuery ||
      host.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      host.mac?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(filteredHosts.map((h) => h.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedRows([...selectedRows, id]);
    } else {
      setSelectedRows(selectedRows.filter((r) => r !== id));
    }
  };

  const handleDeploy = (host: FogHost) => {
    setSelectedHost(host);
    openDeployModal();
  };

  const handleCapture = (host: FogHost) => {
    setSelectedHost(host);
    openCaptureModal();
  };

  const handleViewDetails = (host: FogHost) => {
    setSelectedHost(host);
    openDrawer();
  };

  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <FogLoadingOverlay message="Loading FOG Hosts..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load FOG hosts: {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container
      size="xl"
      py="md"
      style={{
        opacity: pageVisible ? 1 : 0,
        transform: pageVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>FOG Hosts</Title>
          <Text c="dimmed" size="sm">
            {hosts.length} hosts registered in FOG
          </Text>
        </div>
        <Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={isRefetching}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Search & Filters */}
      <Paper withBorder p="md" mb="md" radius="md">
        <Group>
          <TextInput
            placeholder="Search by name or MAC..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
          />
          {selectedRows.length > 0 && (
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={() => {
                const firstHost = hosts.find((h) => h.id === selectedRows[0]);
                if (firstHost) handleDeploy(firstHost);
              }}
            >
              Deploy to {selectedRows.length} host{selectedRows.length > 1 ? 's' : ''}
            </Button>
          )}
        </Group>
      </Paper>

      {/* Hosts Table */}
      <Paper withBorder radius="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>
                <Checkbox
                  checked={selectedRows.length === filteredHosts.length && filteredHosts.length > 0}
                  indeterminate={selectedRows.length > 0 && selectedRows.length < filteredHosts.length}
                  onChange={(e) => handleSelectAll(e.currentTarget.checked)}
                />
              </Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>MAC Address</Table.Th>
              <Table.Th>Assigned Image</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th w={60}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredHosts.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text ta="center" c="dimmed" py="xl">
                    No hosts found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              filteredHosts.map((host, index) => (
                <Table.Tr
                  key={host.id}
                  style={{
                    opacity: visibleRows.has(index) ? 1 : 0,
                    transform: visibleRows.has(index) ? 'translateX(0)' : 'translateX(-10px)',
                    transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleViewDetails(host)}
                >
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRows.includes(host.id)}
                      onChange={(e) => handleSelectRow(host.id, e.currentTarget.checked)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <IconDeviceDesktop size={16} />
                      <Text fw={500}>{host.name}</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {host.mac || host.macs?.[0] || '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {host.imageName ? (
                      <Badge variant="light" color="blue">
                        {host.imageName}
                      </Badge>
                    ) : (
                      <Text c="dimmed" size="sm">
                        No image
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {host.pending ? (
                      <Badge color="yellow">Pending</Badge>
                    ) : host.deployed ? (
                      <Badge color="green">Deployed</Badge>
                    ) : (
                      <Badge color="gray">Ready</Badge>
                    )}
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEye size={14} />}
                          onClick={() => handleViewDetails(host)}
                        >
                          View Details
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconDownload size={14} />}
                          onClick={() => handleDeploy(host)}
                        >
                          Deploy Image
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconUpload size={14} />}
                          onClick={() => handleCapture(host)}
                        >
                          Capture Image
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Deploy Modal */}
      <DeployImageModal
        opened={deployModalOpen}
        onClose={closeDeployModal}
        host={selectedHost}
        images={images}
      />

      {/* Capture Modal */}
      <CaptureImageModal
        opened={captureModalOpen}
        onClose={closeCaptureModal}
        host={selectedHost}
      />

      {/* Host Details Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="Host Details"
        position="right"
        size="lg"
      >
        {selectedHost && (
          <Stack gap="md">
            <Paper withBorder p="md" radius="md">
              <Group gap="xs" mb="xs">
                <IconDeviceDesktop size={20} />
                <Title order={4}>{selectedHost.name}</Title>
              </Group>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">ID</Text>
                  <Text size="sm">{selectedHost.id}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">MAC Address</Text>
                  <Text size="sm" ff="monospace">{selectedHost.mac || selectedHost.macs?.[0] || '-'}</Text>
                </Group>
                {selectedHost.ip && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">IP Address</Text>
                    <Text size="sm">{selectedHost.ip}</Text>
                  </Group>
                )}
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Assigned Image</Text>
                  <Text size="sm">{selectedHost.imageName || 'None'}</Text>
                </Group>
                {selectedHost.description && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Description</Text>
                    <Text size="sm">{selectedHost.description}</Text>
                  </Group>
                )}
                {selectedHost.createdTime && (
                  <Group justify="space-between">
                    <Text size="sm" c="dimmed">Created</Text>
                    <Text size="sm">{new Date(selectedHost.createdTime).toLocaleString()}</Text>
                  </Group>
                )}
              </Stack>
            </Paper>

            <Group>
              <Button
                leftSection={<IconDownload size={16} />}
                onClick={() => {
                  closeDrawer();
                  handleDeploy(selectedHost);
                }}
              >
                Deploy Image
              </Button>
              <Button
                variant="light"
                leftSection={<IconUpload size={16} />}
                onClick={() => {
                  closeDrawer();
                  handleCapture(selectedHost);
                }}
              >
                Capture Image
              </Button>
            </Group>
          </Stack>
        )}
      </Drawer>
    </Container>
  );
}
